// Shared support-resources catalog grid — the Employee portal
// (`EmployeeResourcesCard.tsx`) and the Manager view (`ManagerResources.tsx`)
// read the identical underlying catalog (`resourcesRepoFor(...).listForOrg()`,
// exposed per role at `/api/employee/resources` / `/api/manager/resources`)
// and only differ in surrounding page copy/heading level and the "when to
// use" framing (employee: personal; manager: signposting) — this owns the
// query + card grid + loading/error/empty states; callers own their own
// `<header>` (heading level differs: Employee embeds this as a section
// inside a larger page, Manager is a standalone page).

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type ResourceCard, type ResourceCategory } from '@besideu/shared/resources';
import { api } from '@/lib/api.ts';
import { StatusPill } from '@/components/ui/StatusPill.tsx';
import {
  HeartIcon,
  PulseIcon,
  LifebuoyIcon,
  ShieldIcon,
  BookIcon,
  DocIcon,
} from '@/components/ui/icons.tsx';

const CATEGORY_ICON: Record<ResourceCategory, ReactNode> = {
  internal: <ShieldIcon />,
  bereavement: <HeartIcon />,
  mental_health: <PulseIcon />,
  crisis: <LifebuoyIcon />,
  workplace: <BookIcon />,
  practical: <DocIcon />,
};

export function ResourceCatalogGrid({
  endpoint,
  queryKey,
  whenToUseLabel = 'When to use it:',
  footer,
}: {
  endpoint: string;
  queryKey: readonly unknown[];
  whenToUseLabel?: string;
  footer?: ReactNode;
}) {
  const resources = useQuery({
    queryKey,
    queryFn: () => api.get<{ resources: ResourceCard[] }>(endpoint),
  });

  if (resources.isPending) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (resources.error || !resources.data) {
    return (
      <p className="text-sm text-muted-foreground">
        Could not load resources right now. Try refreshing.
      </p>
    );
  }
  if (resources.data.resources.length === 0) {
    return <p className="text-sm text-muted-foreground">No resources are available yet.</p>;
  }

  return (
    <>
      {/* Flat grid, ordered by the §15.1 category order the API returns —
          no category headings (the design presents one calm grid). */}
      <ul className="grid gap-4 sm:grid-cols-2">
        {resources.data.resources.map((r) => (
          <ResourceItem key={r.id} resource={r} whenToUseLabel={whenToUseLabel} />
        ))}
      </ul>
      {footer}
    </>
  );
}

function ResourceItem({
  resource: r,
  whenToUseLabel,
}: {
  resource: ResourceCard;
  whenToUseLabel: string;
}) {
  return (
    <li className="flex h-full flex-col rounded-lg border border-border bg-card p-5">
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary-soft text-primary"
        >
          {CATEGORY_ICON[r.category]}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-foreground">{r.name}</p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {r.isExternal ? (
              <StatusPill tone="info">External</StatusPill>
            ) : (
              <StatusPill tone="positive">Available now</StatusPill>
            )}
            {r.isConfidential ? <StatusPill tone="neutral">Confidential</StatusPill> : null}
          </div>
        </div>
      </div>

      <p className="mt-3 text-sm text-muted-foreground">{r.description}</p>
      {r.whenToUse ? (
        <p className="mt-2 text-xs text-muted-foreground">
          <span className="font-medium text-foreground/70">{whenToUseLabel} </span>
          {r.whenToUse}
        </p>
      ) : null}

      {r.contactPhone || r.contactUrl ? (
        <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-3 text-sm">
          {r.contactPhone ? (
            <a className="font-medium text-primary hover:underline" href={telHref(r.contactPhone)}>
              {r.contactPhone}
            </a>
          ) : null}
          {r.contactUrl ? (
            <a
              className="font-medium text-primary hover:underline"
              href={r.contactUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Visit website
            </a>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

// Build a `tel:` href from a display phone number. Strips spaces; if the value
// is free text (e.g. "Text SHOUT to 85258"), keep only digits — a `tel:` with
// digits still works, and the visible label keeps the human instruction.
function telHref(phone: string): string {
  const digits = phone.replace(/[^\d+]/g, '');
  return `tel:${digits}`;
}
